import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { chargingApi, type PayFrom } from '@/api/charging';
import {
  pointsApi,
  type ConnectorOut,
  type PointDetailOut
} from '@/api/points';
import { walletApi, walletNumbers, type WalletOut } from '@/api/wallet';
import { stationsApi } from '@/api/stations';
import { SiteGuardModal } from '@/components/location/SiteGuardModal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Display, Eyebrow, MoneyDisplay } from '@/components/ui/Typography';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import { ConnectorSelectCard } from '@/components/points/ConnectorSelectCard';
import { formatBRL } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';
import { useDevHardwareToggle } from '@/hooks/useDevHardwareToggle';
import { useUserPosition } from '@/hooks/useUserPosition';
import {
  checkSite,
  readFix,
  siteCoords,
  type Coords,
  type Fix,
  type SiteCheck
} from '@/lib/location';

// São DOIS mínimos diferentes desde o fim da reserva (migration 050): pagar
// com SALDO exige `min_balance_brl` (~R$ 25, via `walletNumbers`) porque a
// sessão não bloqueia mais nada e pode terminar em dívida; pagar com
// CASHBACK segue no mínimo operacional (`min_reserve_brl`, R$ 1) — o bônus é
// hermético e a recarga simplesmente para quando ele acaba. Não unificar: é
// o que mantém de pé a matriz "saldo zerado + cashback cheio ainda carrega".
//
// Fallback do mínimo do cashback pra quando o backend ainda não expõe
// `min_reserve_brl` no GET /wallet (bundle novo + backend antigo). A regra
// real vem do sistema (BOW_CHARGING_MIN_RESERVE_BRL).
const MIN_RESERVE_FALLBACK_BRL = 1;
// Polling do detalhe do ponto pra refletir mudança de estado do conector
// (Available → Preparing/Charging/Faulted) sem o usuário ter que sair e
// voltar. Mesmo motivo do StationDetail: WS de charger.status.changed
// ainda não é confiável, então puxamos por HTTP.
const POINT_POLL_MS = 5_000;
// Primeiro re-tick logo após abrir: se o fetch do mount pegou o Hub num
// soluço (state null) ou um estado ainda assentando, corrige em ~1s em vez
// de esperar o ciclo cheio de 5s (feedback Saulo 2026-07-04, sintoma 1).
const POINT_FIRST_TICK_MS = 1_200;
// Quanto o toque em "Iniciar" espera pela posição/eletroposto antes de decidir
// a trava (2026-10-08). Estourou → decide com o que tiver.
const SITE_CHECK_WAIT_MS = 4_000;

interface SiteInfo {
  name: string;
  coords: Coords | null;
}

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((resolve) => window.setTimeout(() => resolve(fallback), ms))
  ]);
}

/**
 * Merge do poll: tick sem estado (Hub instável → `state: null`) NÃO degrada o
 * card pra "Sem comunicação" — mantém o último estado conhecido por conector
 * (flicker reportado 2026-07-04, sintoma 2). Null só prevalece quando nunca
 * houve leitura (Hub genuinamente fora desde o início).
 */
function mergeLiveStates(
  prev: PointDetailOut | null,
  next: PointDetailOut
): PointDetailOut {
  if (!prev) return next;
  return {
    ...next,
    connectors: next.connectors.map((c) => {
      if (c.state !== null) return c;
      const before = prev.connectors.find((b) => b.id === c.id);
      return before && before.state !== null
        ? { ...c, state: before.state, soc_percent: before.soc_percent }
        : c;
    })
  };
}

export default function ConfigureCharge() {
  const { pointId, connectorId: urlConnectorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Mensagem vinda do auto-start (QRHandler) quando o start falhou — aparece
  // no banner padrão sem o usuário precisar re-tentar pra ver o motivo.
  const autoStartError =
    (location.state as { autoStartError?: string } | null)?.autoStartError ?? null;

  const [point, setPoint] = useState<PointDetailOut | null>(null);
  const [wallet, setWallet] = useState<WalletOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(autoStartError);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | undefined>(
    undefined
  );
  // Carteira que paga a sessão (Bow Carteiras, T4). Default saldo.
  const [payFrom, setPayFrom] = useState<PayFrom>('cash');
  const dev = useDevHardwareToggle();

  // Trava de "eletroposto errado" (2026-10-08). Quem chegou pelo QR está no
  // carregador por definição — sem trava. Pelo mapa/lista, compara a posição
  // de AGORA com a do eletroposto antes de ligar o carregador.
  const viaQr =
    autoStartError !== null ||
    (location.state as { viaQr?: boolean } | null)?.viaQr === true;
  const position = useUserPosition({ fresh: true });
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [guard, setGuard] = useState<SiteCheck | null>(null);
  const [verifying, setVerifying] = useState(false);
  // O toque em "Iniciar" espera esta consulta (com teto) — senão quem toca
  // rápido passaria sem trava.
  const siteLookup = useRef<Promise<SiteInfo | null>>(Promise.resolve(null));

  // Nome e coordenada do ELETROPOSTO: o ponto (carregador) quase nunca tem
  // lat/lng própria — a fonte de verdade desde a migration 013 é `stations`.
  // Best-effort: falhou → sem nome no topo e sem trava, nunca bloqueia.
  useEffect(() => {
    if (!pointId) return;
    let cancelled = false;
    siteLookup.current = stationsApi
      .listDetailed()
      .then((list) => {
        const st = list.find((s) => s.chargers.some((c) => c.id === pointId));
        const info = st
          ? { name: st.name, coords: siteCoords(st.latitude, st.longitude) }
          : null;
        if (!cancelled) setSite(info);
        return info;
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [pointId]);

  useEffect(() => {
    if (!pointId || !urlConnectorId) return;
    let cancelled = false;

    (async () => {
      try {
        const [pointDetail, walletData, activeSession] = await Promise.all([
          pointsApi.get(pointId),
          walletApi.get(),
          chargingApi.active()
        ]);
        if (cancelled) return;
        if (activeSession) {
          navigate(`/charging/${activeSession.id}`, { replace: true });
          return;
        }
        setPoint(pointDetail);
        setWallet(walletData);
        // Pré-seleciona o conector que veio da URL — só se ele estiver
        // selecionável (cabo em uso não fica pré-marcado). Fallback pro único
        // cabo só se ele também for selecionável.
        const fromUrl = pointDetail.connectors.find(
          (c) => c.id === urlConnectorId
        );
        const single =
          pointDetail.connectors.length === 1
            ? pointDetail.connectors[0]
            : undefined;
        // Ponto de conector ÚNICO: pré-seleciona SEMPRE, mesmo indisponível —
        // o card + CTA "Cabo indisponível" comunicam o estado honestamente.
        // Antes, conector UNAVAILABLE caía no "Conector não encontrado neste
        // ponto" (bug da cliente 2026-07-02, reproduzido no dev 2026-07-04:
        // Riseon AC com o único Type 2 fora do ar). Multi-conector mantém a
        // regra original: cabo em uso não fica pré-marcado.
        const preselect =
          single ??
          (fromUrl && isChargerSelectable(fromUrl.state) ? fromUrl : undefined);
        setSelectedConnectorId(preselect?.id);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(friendlyMessage(err, 'Falha ao carregar'));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pointId, urlConnectorId, navigate]);

  // Refresh silencioso do ponto — mantém `connector.state` vivo enquanto
  // o usuário está nessa tela. Sem skeleton, sem reset de seleção.
  useEffect(() => {
    if (!pointId) return;
    let cancelled = false;
    const tick = () => {
      pointsApi
        .get(pointId)
        .then((p) => {
          if (cancelled) return;
          setPoint((prev) => mergeLiveStates(prev, p));
        })
        .catch(() => {
          // silencioso — não substitui o erro inicial nem o último estado bom
        });
    };
    const early = window.setTimeout(tick, POINT_FIRST_TICK_MS);
    const id = window.setInterval(tick, POINT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(early);
      window.clearInterval(id);
    };
  }, [pointId]);

  const connector: ConnectorOut | undefined = useMemo(
    () => point?.connectors.find((c) => c.id === selectedConnectorId),
    [point, selectedConnectorId]
  );

  if (loading) {
    return <div className="h-48 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }
  if (error) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        {error}
      </div>
    );
  }
  if (!point || !wallet) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        Ponto não encontrado.
      </div>
    );
  }

  const hasMultiple = point.connectors.length > 1;
  // Só dispara com ponto SEM conector nenhum (dado incompleto no backend) —
  // conector único é sempre pré-selecionado no load, mesmo indisponível.
  if (!hasMultiple && !connector) {
    return (
      <WarningCard
        title="Carregador indisponível"
        message="Este carregador está sem conectores cadastrados no momento. Tente outro ponto ou fale com o suporte."
      />
    );
  }

  const tariff = Number(point.tariff_per_kwh_brl ?? 0);

  if (tariff <= 0) {
    return (
      <WarningCard
        title="Tarifa indisponível"
        message="Este ponto está sem tarifa ativa. Tente outro ponto ou fale com o suporte."
      />
    );
  }

  // Estados das carteiras (T4/T8d): saldo pode ser NEGATIVO (pendência) —
  // nesse estado as DUAS opções ficam com cadeado; cashback é hermético e
  // nunca fica negativo. A margem que originou a pendência NUNCA aparece.
  // `minBalance` vem do helper (campo novo `min_balance_brl` do GET /wallet,
  // com o fallback do default do backend lá dentro).
  const { available, bonusAvailable, debt, minBalance } = walletNumbers(wallet);
  const minReserve = Number(wallet.min_reserve_brl ?? MIN_RESERVE_FALLBACK_BRL);
  const inDebt = debt > 0;
  // Gates diferentes por carteira: saldo exige o mínimo ALTO (a sessão não
  // reserva nada e pode fechar em dívida); cashback fica no operacional.
  const cashSelectable = !inDebt && available >= minBalance;
  const bonusSelectable = !inDebt && bonusAvailable >= minReserve;
  // Saldo abaixo do mínimo (mas sem pendência): estado esperado de conta
  // nova — a tela renderiza normal e o CTA vira "Adicionar crédito", A MENOS
  // que o cashback cubra o mínimo dele (aí dá pra iniciar só com ele — T8b).
  const cashLow = !inDebt && available >= 0 && available < minBalance;
  const bonusZeroed = !inDebt && bonusAvailable < minReserve;
  const needsTopUp = !inDebt && !cashSelectable && !bonusSelectable;
  // Seleção efetiva: se a carteira marcada ficou inselecionável e a outra
  // cobre o mínimo, a seleção cai pra outra (ex.: saldo zerado + cashback
  // cheio → cashback já vem marcado).
  const payFromEffective: PayFrom =
    payFrom === 'cash' && !cashSelectable && bonusSelectable
      ? 'bonus'
      : payFrom === 'bonus' && !bonusSelectable && cashSelectable
        ? 'cash'
        : payFrom;
  const minTopup =
    wallet.min_topup_brl != null ? Number(wallet.min_topup_brl) : debt;

  const canStart = !!connector && isChargerSelectable(connector.state);
  const returnTo = `/configure/${pointId}/${urlConnectorId}`;

  async function onStart() {
    if (!pointId || !selectedConnectorId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const session = await chargingApi.start({
        point_id: pointId,
        connector_id: selectedConnectorId,
        limit_type: 'full_balance',
        limit_value: '0',
        pay_from: payFromEffective
      });
      navigate(`/charging/${session.id}`);
    } catch (err) {
      setSubmitError(friendlyMessage(err, 'Falha ao iniciar'));
      setSubmitting(false);
      // Re-lê a carteira depois de uma falha: o motivo mais comum é o estado
      // financeiro ter MUDADO no servidor (409 `wallet_in_debt` de uma sessão
      // anterior que fechou em dívida, saldo que caiu abaixo do mínimo). Sem
      // esse refresh a tela continua desenhando o estado pré-dívida — CTA
      // "Iniciar recarga" habilitado — e o cliente re-tenta em loop.
      // Best-effort: falha aqui não pode mascarar o erro original.
      try {
        const fresh = await walletApi.get();
        setWallet(fresh);
      } catch {
        // silencioso — a mensagem do start já está na tela
      }
    }
  }

  const stationName = site?.name ?? point.name;
  const pointXY = siteCoords(point.latitude, point.longitude);
  const siteXY = pointXY ?? site?.coords ?? null;

  // Perto → inicia direto (zero toque extra pra quem está no lugar certo).
  // Longe ou sem localização → modal de confirmação.
  async function onStartClick() {
    if (viaQr) {
      void onStart();
      return;
    }
    setVerifying(true);
    const info = site ?? (await withTimeout(siteLookup.current, SITE_CHECK_WAIT_MS, null));
    const target = pointXY ?? info?.coords ?? null;
    let fix: Fix | null = position.fix;
    if (target && !fix && position.status === 'granted') {
      // Permissão dada e leitura ainda em curso: espera um pouco antes de
      // incomodar o cliente com o modal.
      fix = await withTimeout(readFix(true), SITE_CHECK_WAIT_MS, null);
    }
    setVerifying(false);
    // Eletroposto sem coordenada cadastrada: não há como julgar — segue.
    const check = target ? checkSite(fix, target) : null;
    if (!check || check.kind === 'near') {
      void onStart();
      return;
    }
    setGuard(check);
  }

  // Sem localização o modal recalcula ao vivo: o cliente ativa a localização
  // ali dentro e o aviso vira "você está aqui" ou "você está longe".
  const guardShown: SiteCheck | null =
    guard === null ? null : guard.kind === 'unknown' ? checkSite(position.fix, siteXY) : guard;

  // NSS: viewport fixa (100dvh − BottomNav), CTA ancorado no rodapé; miolo
  // rola só com fonte de acessibilidade grande. Redesign Carteiras (T4):
  // o card navy de saldo/estimativa saiu — entram a linha leve de tarifa e
  // o bloco "Pagar com" (Saldo × Cashback).
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col px-5"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        {/* Header com voltar contextual (feedback cliente nº 10 + mock NSS) */}
        <div className="flex flex-shrink-0 items-center gap-2.5 pb-3 pt-1 [@media(max-height:740px)]:pb-2.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
            aria-label="Voltar"
          >
            <ChevronLeftIcon />
          </button>
          <div className="min-w-0">
            {/* Nome do eletroposto no topo: o cliente vê ONDE vai ligar o
                carregador antes de tocar em iniciar (2026-10-08). */}
            <Eyebrow className="block truncate">{site?.name ?? 'Configurar'}</Eyebrow>
            <Display
              size={26}
              className="mt-0.5 [@media(max-height:740px)]:!text-[23px]"
            >
              Iniciar recarga
            </Display>
          </div>
        </div>

        {/* Miolo — único bloco que pode rolar (fonte de acessibilidade) */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [@media(max-height:740px)]:gap-3">
          {/* Seleção de conector — mesmo card da landing (design V5 · A), com
              o conector escolhido já marcado. Título igual ao da landing. */}
          <div className="flex-shrink-0">
            <span className="font-display text-[17px] font-medium tracking-[-0.015em] text-bow-royal [@media(max-height:740px)]:text-[15px]">
              Selecione o conector
            </span>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5 [@media(max-height:740px)]:mt-2">
              {point.connectors.map((c) => (
                <ConnectorSelectCard
                  key={c.id}
                  connector={c}
                  selected={c.id === selectedConnectorId}
                  disabled={!isChargerSelectable(c.state)}
                  onClick={() => setSelectedConnectorId(c.id)}
                  letterLabel={point.capabilities?.['connector_letters'] === true}
                />
              ))}
            </div>
          </div>

          {/* Linha de tarifa leve (substitui o card navy do design anterior) */}
          <div
            className="flex flex-shrink-0 items-center justify-between rounded-[18px] px-4 py-[13px]"
            style={{
              background: 'rgba(0,27,116,0.04)',
              border: '1px solid rgba(0,27,116,0.09)'
            }}
          >
            <span className="text-[13px]" style={{ color: 'rgba(0,27,116,0.62)' }}>
              Tarifa
            </span>
            <span className="text-[14.5px] font-semibold text-bow-royal">
              R$ {tariff.toFixed(2).replace('.', ',')} por kWh
            </span>
          </div>

          {/* Pendência: recarga bloqueada até o próximo aporte (T4e) */}
          {inDebt && (
            <div
              className="flex flex-shrink-0 items-center gap-3 rounded-[18px] px-4 py-3"
              style={{
                background: 'rgba(238,38,62,0.06)',
                border: '1px solid rgba(238,38,62,0.18)'
              }}
            >
              <span
                className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[13px]"
                style={{ background: 'rgba(238,38,62,0.12)' }}
              >
                <WarnIcon />
              </span>
              <span className="flex min-w-0 flex-col">
                <span
                  className="text-[13.5px] font-semibold"
                  style={{ color: '#D5152B' }}
                >
                  Adicione saldo pra voltar a carregar
                </span>
                <span className="text-[12px]" style={{ color: 'rgba(0,27,116,0.6)' }}>
                  Recarga bloqueada · saldo −{formatBRL(debt)}
                </span>
              </span>
            </div>
          )}

          {/* Pagar com — Saldo × Cashback (radio-cards) */}
          <div className="flex-shrink-0">
            <Eyebrow>Pagar com</Eyebrow>
            <div className="mt-2 flex flex-col gap-2">
              <PayRadio
                kind="cash"
                selected={payFromEffective === 'cash' && cashSelectable}
                selectable={cashSelectable}
                locked={inDebt}
                dimmed={false}
                value={available}
                onSelect={() => setPayFrom('cash')}
                note={
                  cashLow ? (
                    <span
                      className="mt-2 block w-full rounded-lg px-2.5 py-1.5 text-[11px] font-medium"
                      style={{
                        background: 'rgba(222,124,0,0.10)',
                        color: '#B26205'
                      }}
                    >
                      Adicione saldo pra iniciar — mínimo{' '}
                      {formatBRL(minBalance)}
                    </span>
                  ) : undefined
                }
              />
              <PayRadio
                kind="bonus"
                selected={payFromEffective === 'bonus' && bonusSelectable}
                selectable={bonusSelectable}
                locked={inDebt}
                dimmed={bonusZeroed}
                value={bonusAvailable}
                onSelect={() => setPayFrom('bonus')}
              />
            </div>
            {bonusZeroed && (
              <p className="mt-2 px-1 text-[11.5px] leading-snug text-bow-royal/55">
                Você ganha cashback adicionando saldo — ele cobre recargas
                inteiras quando tiver valor.
              </p>
            )}
          </div>

          {submitError && (
            <p className="flex-shrink-0 px-1 text-sm text-bow-red">{submitError}</p>
          )}
        </div>

        {/* Rodapé ancorado: CTA nunca sai da tela (NSS).
            O card de dev mora AQUI (não no miolo rolável): abaixo da dobra do
            scroll interno ele "sumia" — feedback Saulo 2026-07-04. */}
        <div className="flex flex-shrink-0 flex-col gap-2.5 pb-2.5 pt-2.5 [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:pt-2">
          {dev.isDev && (
            <Card className="border-2 border-dashed border-bow-yellow/40 bg-bow-yellow/5 p-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={dev.useHardware}
                  onChange={(e) => dev.setUseHardware(e.target.checked)}
                  className="mt-0.5 h-5 w-5 cursor-pointer accent-bow-royal"
                />
                <span className="flex flex-col text-[13px]">
                  <span className="font-semibold text-bow-royal">
                    Usar hardware real ({dev.useHardware ? 'ON' : 'OFF'})
                  </span>
                  <span className="text-xs text-bow-royal/65">
                    {dev.useHardware
                      ? '⚠️ Ao iniciar, o relé do carregador físico VAI LIGAR. Confirme que o cabo está plugado num carro.'
                      : 'Hub simula MeterValues. Carregador físico não é acionado — fluxo só na UI.'}
                  </span>
                </span>
              </label>
            </Card>
          )}

          {inDebt ? (
            /* Aporte mínimo = dívida (min_topup_brl). `from` traz o usuário
               de volta pra cá depois do PIX pago. */
            <Button
              onClick={() =>
                navigate('/wallet/recharge', { state: { from: returnTo } })
              }
            >
              Adicionar saldo · mínimo {formatBRL(minTopup)}
            </Button>
          ) : needsTopUp ? (
            /* `from` leva o usuário DE VOLTA pra cá depois do PIX pago —
               "continuar onde parei" (feedback Saulo 2026-07-04). */
            <Button
              onClick={() =>
                navigate('/wallet/recharge', { state: { from: returnTo } })
              }
            >
              Adicionar crédito
            </Button>
          ) : (
            <Button
              onClick={() => void onStartClick()}
              disabled={submitting || verifying || !canStart}
            >
              <BoltGlyph size={18} />
              {submitting
                ? 'Iniciando…'
                : verifying
                  ? 'Conferindo localização…'
                  : !connector
                  ? 'Selecione um conector'
                  : !canStart
                    ? 'Cabo indisponível'
                    : 'Iniciar recarga'}
            </Button>
          )}
        </div>
      </div>

      {guardShown && (
        <SiteGuardModal
          stationName={stationName}
          check={guardShown}
          position={position}
          onConfirm={() => {
            setGuard(null);
            void onStart();
          }}
          onPickAnother={() => navigate('/', { replace: true })}
          onClose={() => setGuard(null)}
        />
      )}
    </div>
  );
}

interface PayRadioProps {
  kind: PayFrom;
  /** Radio marcado (só acontece com selectable=true). */
  selected: boolean;
  /** Pode ser tocado/selecionado. */
  selectable: boolean;
  /** Pendência: cadeado no lugar do radio, opacity normal. */
  locked: boolean;
  /** Cashback zerado: opacity .55, radio off. */
  dimmed: boolean;
  value: number;
  note?: ReactNode;
  onSelect: () => void;
}

/**
 * Radio-card de carteira (Bow Carteiras): Saldo (navy) × Cashback (badge $
 * roxo). Estados: selecionado (borda da cor da carteira + sombra leve),
 * dimmed (cashback sem valor), locked (pendência — cadeado, sem radio;
 * o card do saldo mostra o valor negativo em vermelho).
 */
function PayRadio({
  kind,
  selected,
  selectable,
  locked,
  dimmed,
  value,
  note,
  onSelect
}: PayRadioProps) {
  const isBonus = kind === 'bonus';
  const accent = isBonus ? '#A23C92' : '#001B74';
  return (
    <button
      type="button"
      disabled={!selectable}
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex w-full flex-col rounded-[20px] bg-white px-4 py-[13px] text-left transition disabled:cursor-default ${
        dimmed ? 'opacity-[0.55]' : ''
      }`}
      style={{
        border: `1.5px solid ${selected ? accent : 'rgba(0,27,116,0.10)'}`,
        boxShadow: selected
          ? isBonus
            ? '0 4px 14px rgba(162,60,146,0.12)'
            : '0 4px 14px rgba(0,27,116,0.10)'
          : undefined
      }}
    >
      <span className="flex w-full items-center gap-3">
        {isBonus ? (
          <span
            aria-hidden
            className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full text-white"
            style={{
              background: 'linear-gradient(135deg,#835DD0,#C04CAE)',
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 18
            }}
          >
            $
          </span>
        ) : (
          <span
            aria-hidden
            className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[13px]"
            style={{ background: 'rgba(0,27,116,0.07)' }}
          >
            <WalletIcon />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className="text-[14px] font-semibold"
            style={{ color: isBonus ? '#A23C92' : '#001B74' }}
          >
            {isBonus ? 'Cashback' : 'Saldo'}
          </span>
          {value < 0 ? (
            <span
              className="text-bow-red"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 20,
                lineHeight: 1.15,
                letterSpacing: '-0.03em'
              }}
            >
              − {formatBRL(-value)}
            </span>
          ) : (
            <MoneyDisplay value={value} size={20} weight={600} className="text-bow-royal" />
          )}
        </span>
        {locked ? (
          <LockIcon />
        ) : (
          <span
            aria-hidden
            className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full"
            style={
              selected
                ? { background: accent }
                : { border: '1.5px solid rgba(0,27,116,0.25)' }
            }
          >
            {selected && <CheckIcon />}
          </span>
        )}
      </span>
      {note}
    </button>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function BoltGlyph({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

/** Ícone padrão de carteira do app (mesmo traço do item "Saldo" da nav). */
function WalletIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="#001B74"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="6" width="18" height="14" rx="3" />
      <path d="M16 13h2" />
      <path d="M3 10h18" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="rgba(0,27,116,0.35)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="flex-shrink-0"
    >
      <rect x="5" y="11" width="14" height="9" rx="2.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={13}
      height={13}
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5 9-10" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="#D5152B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3 2.5 20h19L12 3z" />
      <path d="M12 9.5V14" />
      <path d="M12 17.2h.01" />
    </svg>
  );
}

function WarningCard({ title, message }: { title: string; message: string }) {
  return (
    <Card className="flex flex-col gap-2 p-5">
      <span className="font-semibold">{title}</span>
      <span className="text-sm text-bow-royal/65">{message}</span>
    </Card>
  );
}
